import type { FC, ReactNode } from 'react'

import { ChevronLeft, ChevronRight } from 'lucide-react'
import { formatNumber } from '@/lib/format'
import { Button } from '@/components/ui/button'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select'

interface TablePaginationProps {
    page: number
    pageSize: number
    total: number
    noun: string
    onPageChange: (page: number) => void
    onPageSizeChange: (pageSize: number) => void
    pageSizes?: number[]
}

export const TablePagination: FC<TablePaginationProps> = ({
    page,
    pageSize,
    total,
    noun,
    onPageChange,
    onPageSizeChange,
    pageSizes = [10, 20, 50]
}): ReactNode => {
    const pageCount = Math.max(1, Math.ceil(total / pageSize))
    const from = total === 0 ? 0 : (page - 1) * pageSize + 1
    const to = Math.min(total, page * pageSize)
    const sizeItems = pageSizes.map((size) => ({ value: String(size), label: String(size) }))

    return (
        <div className='flex flex-col gap-3 text-sm sm:flex-row sm:items-center sm:justify-between'>
            <p className='text-muted-foreground' aria-live='polite'>
                Menampilkan <span className='font-medium text-foreground tabular-nums'>{formatNumber(from)}</span>
                <span aria-hidden='true'>{'–'}</span>
                <span className='sr-only'> sampai </span>
                <span className='font-medium text-foreground tabular-nums'>{formatNumber(to)}</span> dari{' '}
                <span className='font-medium text-foreground tabular-nums'>{formatNumber(total)}</span> {noun}
            </p>

            <div className='flex items-center gap-2'>
                <span className='hidden text-muted-foreground sm:inline'>
                    Baris
                </span>

                <Select
                    items={sizeItems}
                    value={String(pageSize)}
                    onValueChange={(value) => {
                        if (value) onPageSizeChange(Number(value))
                    }}
                >
                    <SelectTrigger size='sm' className='w-[72px]' aria-label='Jumlah baris per halaman'>
                        <SelectValue />
                    </SelectTrigger>

                    <SelectContent>
                        {sizeItems.map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                                {item.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <span className='px-1 text-muted-foreground tabular-nums'>
                    {formatNumber(page)} / {formatNumber(pageCount)}
                </span>

                <Button
                    variant='outline'
                    size='icon-sm'
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                    aria-label='Halaman sebelumnya'
                >
                    <ChevronLeft />
                </Button>

                <Button
                    variant='outline'
                    size='icon-sm'
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= pageCount}
                    aria-label='Halaman berikutnya'
                >
                    <ChevronRight />
                </Button>
            </div>
        </div>
    )
}
